import type { ComponentType, ReactNode } from 'react';

export const enum CommandBarPriority {
	VeryLow = -100,
	Low = -50,
	Normal = 0,
	High = 50,
	VeryHigh = 100,
}

/**
 * Props passed to every command bar provider component.
 * Providers receive the current search query and a function to close the command bar.
 */
export interface CommandBarProviderProps {
	/** The current search query in the command bar input */
	search: string;

	/** Call this to close the command bar after executing a command */
	onClose: () => void;
}

/**
 * Configuration for a command bar provider.
 * Providers are React components that render Command.Group and Command.Item elements.
 */
export interface CommandBarProviderConfig<TProps extends CommandBarProviderProps = CommandBarProviderProps> {
	/** Unique identifier for this provider */
	id: string;

	/**
	 * Optional function to compute relevance for the current search.
	 * Higher scores = rendered first. Return 0 or negative to indicate no match.
	 * If not provided, defaults to 0 (neutral).
	 */
	getRelevance?: (search: string) => number;

	/**
	 * Optional prefix to match before calling getRelevance.
	 * If provided, when the search bar starts with this prefix, only providers whose search
	 * prefixes match will be shown.
	 */
	searchPrefix?: string | string[];

	/** The component to render. Will receive CommandBarProviderProps. */
	component: ComponentType<TProps>;

	/**
	 * Additional props to pass to the component.
	 */
	props: Omit<TProps, keyof CommandBarProviderProps>;

	/**
	 * Optional group to place this provider's commands in. This is preferred over Command.Group
	 * inside the provider component because it deduplicates group headings.
	 */
	group?: string;
}

/**
 * History entry stored when a command is executed.
 */
export interface CommandHistoryEntry {
	/** The search query that was active when the command was selected */
	query: string;
	/** Timestamp when the command was executed */
	timestamp: number;
}

/**
 * State shape for the command bar aggregator context.
 */
export interface CommandBarAggregatedState {
	/** Registered provider configurations */
	providers: CommandBarProviderConfig;

	/**
	 * Placeholder text for the input field.
	 * Last registered placeholder wins (allows page-specific customization).
	 */
	placeholder: string;

	/**
	 * Empty state message shown when no results match.
	 * Each registered element should self-filter by returning `null` when its prefix is not active.
	 */
	emptyStateMessage: ReactNode;
}
